import React, { useCallback, useContext, useMemo, useState } from 'react';
import classNames from 'classnames';
import { Field, useForm, useFormState } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { Button, Loader } from 'ncoded-component-library';
import PasswordField from 'components/PasswordField';
import validate from 'validations';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import { isEnterPressed } from 'utils';
import AccountActions from './components/AccountActions';

import './Account.styles.scss';

type AccountProps = {
  className?: string;
};

const Account: React.FC<AccountProps> = (props) => {
  const { className } = props;

  const [isChangingPassword, setIsChangingPassword] = useState(false);

  const { changePassword } = useContext(CurrentUserContext);

  const classes = classNames('account', className);

  const {
    values: { oldPassword, newPassword },
    errors: { newPassword: newPassErr },
  } = useFormState();
  const { batch, change } = useForm();

  const isValidChangePassword = useMemo(
    () =>
      !newPassErr && oldPassword && newPassword && oldPassword !== newPassword,
    [newPassErr, newPassword, oldPassword],
  );

  const { t } = useTranslation();

  const onPasswordChange = useCallback(() => {
    if (!isValidChangePassword) return;

    setIsChangingPassword(true);

    changePassword?.(
      { oldPassword, newPassword },
      {
        onSuccess: () => {
          batch(() => {
            change('oldPassword', '');
            change('newPassword', '');
          });
          setIsChangingPassword(false);
        },
        onError: () => setIsChangingPassword(false),
      },
    );
  }, [
    batch,
    change,
    changePassword,
    isValidChangePassword,
    newPassword,
    oldPassword,
  ]);

  return (
    <div className={classes}>
      <section className="account__password profile-card-container">
        {isChangingPassword && <Loader />}

        <div className="account__password__parahraphs">
          <p className="profile-section-title">{t('password')}</p>
          <p className="description">{t('passwordSectionDescription')}</p>
        </div>

        <div
          className="account__password__fields"
          onKeyDown={(e) => {
            if (isEnterPressed(e)) {
              onPasswordChange();
            }
          }}
        >
          <Field
            name="oldPassword"
            label={t('oldPassword')}
            placeholder={t('enterYourCurrentPassword')}
            component={PasswordField}
            withoutStrengthIndicator
            validate={validate.passwordFieldsValidation()}
          />
          <Field
            name="newPassword"
            label={t('newPassword')}
            placeholder={t('enterYourNewPassword')}
            component={PasswordField}
            validate={validate.passwordFieldsValidation()}
          />
        </div>

        <div className="account__password__actions">
          <Button
            disabled={isChangingPassword || !isValidChangePassword}
            onClick={onPasswordChange}
          >
            {t('changePassword')}
          </Button>
        </div>
      </section>

      <section className="account__actions profile-card-container">
        <p className="profile-section-title">{t('accountActions')}</p>
        <AccountActions />
      </section>
    </div>
  );
};

export default Account;
